import { useEffect, useState } from "react";
import Reveal from "../../common/Reveal";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ArrowPathIcon, PlayIcon, ArrowRightIcon, ChevronDownIcon, PhoneIcon, DocumentIcon, PhotoIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import AiDisclaimer from "../AiDisclaimer";
import Button from "../../common/Button";
import { api, BriefVersionItem, InvestigationDetail } from "../api";
import AnalyzingState from "../AnalyzingState";
import { isNewSince, NewPill, UpdatesBanner } from "../updates";

// The Brief reads like a briefing memo: eyebrow, headline,
// the lead summary, then sections with deliberate hierarchy. Everything is
// generated from the frozen rollup; nothing here is editable.
const BriefTab: React.FC<{ detail: InvestigationDetail; onRefresh: () => Promise<void>; reanalyzing?: boolean; onSeen?: () => void; clearing?: boolean }> = ({ detail, onRefresh, reanalyzing, onSeen, clearing }) => {
  const brief = detail.brief;
  // Optimistic: flip to the working state on click, then defer to the
  // server's rollup job state (the detail poll refreshes every 5s).
  const [requested, setRequested] = useState(false);
  const regenerating = requested || detail.counts.rollupRunning;
  useEffect(() => {
    if (detail.counts.rollupRunning) setRequested(false);
  }, [detail.counts.rollupRunning]);
  useEffect(() => {
    if (!requested) return;
    const t = setTimeout(() => setRequested(false), 20000); // safety valve if the job never shows up
    return () => clearTimeout(t);
  }, [requested]);

  const regenerate = async () => {
    setRequested(true);
    try {
      await api.regenerateBrief(detail.id);
      await onRefresh();
    } catch {
      setRequested(false);
      toast.error("Could not start the brief");
    }
  };

  if (reanalyzing) {
    return <AnalyzingState title="Re-analyzing every call" body="Each call is being read again with the current focus. The brief is rewritten when the last call finishes." />;
  }

  if (regenerating) {
    return (
      <AnalyzingState
        title={brief ? "Regenerating Brief" : "Writing the Brief"}
        body="Reading every analyzed call, the flagged leads, and the people involved, then writing the briefing. This usually takes about a minute."
      />
    );
  }

  if (!brief) {
    return (
      <div className="rounded-2xl border border-dashed border-base-300 p-12 text-center">
        <p className="font-semibold text-base-content">No brief yet</p>
        <p className="mt-1 text-sm text-base-content/60">
          {detail.counts.files === 0
            ? "Upload jail calls and the brief writes itself once they're analyzed."
            : detail.counts.processing > 0
              ? "Calls are still being analyzed. The brief is generated when the batch finishes."
              : "Generate a briefing from the analyzed calls."}
        </p>
        {detail.counts.files > 0 && detail.counts.processing === 0 && (
          <Button variant="primary" className="mt-5" onClick={regenerate}>
            Generate brief
          </Button>
        )}
      </div>
    );
  }

  // Files deleted after the current brief was generated: their content may
  // still be cited above, and the officer chose not to regenerate yet.
  const deletedAfterBrief = (detail.deleted_files || []).filter((d) => new Date(d.deletedAt) > new Date(brief.generatedAt));

  return (
    <article className="space-y-12">
      {/* Masthead */}
      <Reveal as="header" index={0}>
        <div className="flex items-center justify-between gap-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-300">
            Brief{detail.case_number ? ` · Case ${detail.case_number}` : ""}
          </p>
          <Button variant="secondary" size="xs" onClick={regenerate} title="Regenerate">
            <ArrowPathIcon className="size-3.5" /> Regenerate
          </Button>
        </div>
        <h1 className="mt-3 text-[28px] leading-[1.2] font-bold tracking-tight text-base-content text-balance">{brief.headline}</h1>
        <AiDisclaimer kind="brief" className="mt-4" />
        <p className="mt-3 text-xs text-base-content/50">
          Generated {new Date(brief.generatedAt).toLocaleString()}
          {brief.version ? ` · version ${brief.version}` : ""}
        </p>
        {detail.updates.brief > 0 && onSeen && <UpdatesBanner tab="brief" count={detail.updates.brief} since={detail.seenAt.brief} onDismiss={onSeen} clearing={clearing} className="mt-5" />}
        <BriefUpdateLog detail={detail} since={detail.seenAt.brief} clearing={clearing} />
        {deletedAfterBrief.length > 0 && (
          <div className="mt-5 rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50/70 dark:bg-amber-950/20 p-4">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">Deleted after this brief was written</p>
            <p className="mt-1 text-[13px] leading-5 text-amber-800/90 dark:text-amber-400/90">
              The brief may still reference material from{" "}
              {deletedAfterBrief.length === 1 ? "this file" : "these files"} — regenerate it to remove them.
            </p>
            <ul className="mt-2 space-y-0.5 text-[13px] text-amber-900 dark:text-amber-300">
              {deletedAfterBrief.map((d, i) => (
                <li key={i}>
                  <span className="font-medium">{d.fileName}</span>
                  <span className="text-amber-800/70 dark:text-amber-400/70">
                    {" "}
                    — {d.kind.replace("_", " ")}
                    {d.callLabel ? ` (${d.callLabel})` : ""}, deleted {new Date(d.deletedAt).toLocaleDateString()}
                    {d.leadCount ? `, ${d.leadCount} flag${d.leadCount === 1 ? "" : "s"} removed` : ""}
                  </span>
                </li>
              ))}
            </ul>
            <Button variant="secondary" size="sm" className="mt-3" onClick={regenerate}>
              <ArrowPathIcon className="size-3.5 mr-1" /> Update brief
            </Button>
          </div>
        )}
      </Reveal>

      {/* Summary */}
      <Reveal as="section" index={2}>
        <SectionTitle n="01" title="Call summary" />
        <p className="mt-4 text-[16px] leading-8 text-base-content/85 whitespace-pre-wrap">{brief.summary}</p>
      </Reveal>

      {/* Listen first */}
      {brief.listenFirst.length > 0 && (
        <Reveal as="section" index={4}>
          <SectionTitle n="02" title="Top calls to review" />
          <ol className="mt-5 grid gap-x-3 gap-y-6 sm:grid-cols-2">
            {brief.listenFirst.map((l, i) => (
              <li key={l.callId} className="group relative rounded-2xl border border-base-300 bg-base-100 p-5 hover:border-red-300 dark:hover:border-red-800 transition-colors">
                <span className="absolute -top-3 left-5 size-7 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center shadow">{i + 1}</span>
                <p className="mt-2 text-[15px] leading-6 text-base-content/90">{l.reason}</p>
                <Link
                  to={`/investigations/${detail.id}/calls/${l.callId}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-red-700 dark:text-red-400 hover:underline"
                >
                  <PlayIcon className="size-4" /> Open call
                </Link>
              </li>
            ))}
          </ol>
        </Reveal>
      )}

      {/* People */}
      {brief.keyPeople.length > 0 && (
        <Reveal as="section" index={6}>
          <SectionTitle n="03" title="Key individuals" />
          <p className="mt-3 text-sm text-base-content/60">
            The people the calls keep returning to. This is not everyone named;{" "}
            <Link to={`/investigations/${detail.id}/people`} className="text-blue-600 dark:text-blue-300 hover:underline">
              All mentions
            </Link>{" "}
            lists every person, number, address, and vehicle with where each came up.
          </p>
          <ul className="mt-4 divide-y divide-base-300 border-y border-base-300">
            {brief.keyPeople.map((p, i) => (
              <li key={i} className="py-3 grid gap-x-6 gap-y-1 sm:grid-cols-[14rem_1fr]">
                <div className="min-w-0">
                  <p className="font-semibold text-base-content leading-snug">{p.name}</p>
                  <p className="text-xs text-base-content/60 mt-0.5">{p.role}</p>
                </div>
                <p className="text-[15px] leading-6 text-base-content/85">{p.note || ""}</p>
              </li>
            ))}
          </ul>
          <Link to={`/investigations/${detail.id}/people`} className="mt-3 inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-300 hover:underline">
            All mentions <ArrowRightIcon className="size-3.5" />
          </Link>
        </Reveal>
      )}

      {/* Timeline */}
      {brief.timeline.length > 0 && (
        <Reveal as="section" index={8}>
          <SectionTitle n="04" title="Sequence of events" />
          <ol className="mt-5 relative border-l-2 border-base-300 ml-2 space-y-5">
            {brief.timeline.map((t, i) => (
              <li key={i} className="pl-6 relative">
                <span className="absolute -left-[7px] top-1.5 size-3 rounded-full bg-base-100 border-2 border-blue-500" />
                <p className="text-[11px] font-semibold uppercase tracking-wider text-base-content/50 tabular-nums">{t.date}</p>
                <p className="mt-0.5 text-[15px] leading-6 text-base-content/90">
                  {t.description}
                  {t.callId && (
                    <Link to={`/investigations/${detail.id}/calls/${t.callId}`} className="ml-2 text-sm text-blue-600 dark:text-blue-300 hover:underline whitespace-nowrap">
                      open call
                    </Link>
                  )}
                </p>
              </li>
            ))}
          </ol>
        </Reveal>
      )}

      {/* Open questions */}
      {brief.openQuestions.length > 0 && (
        <Reveal as="section" index={10}>
          <SectionTitle n="05" title="Open questions" />
          <ol className="mt-5 rounded-2xl border border-base-300 bg-base-200/50 divide-y divide-base-300">
            {brief.openQuestions.map((q, i) => (
              <li key={i} className="flex gap-4 px-5 py-3.5">
                <span className="text-sm font-semibold tabular-nums text-base-content/40 w-5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-[15px] leading-6 text-base-content/90">{q}</p>
              </li>
            ))}
          </ol>
        </Reveal>
      )}

      <footer className="border-t border-base-300 pt-5">
        <AiDisclaimer kind="brief" size="inline" className="max-w-2xl" />
      </footer>
    </article>
  );
};

const KIND_ICON: Record<string, React.FC<{ className?: string }>> = { jail_call: PhoneIcon, document: DocumentIcon, spreadsheet: TableCellsIcon, photo: PhotoIcon };

function describeAdded(v: BriefVersionItem): string {
  const calls = v.newFiles.filter((f) => f.kind === "jail_call").length;
  const docs = v.newFiles.length - calls;
  const parts = [calls ? `${calls} new call${calls === 1 ? "" : "s"}` : null, docs ? `${docs} new document${docs === 1 ? "" : "s"}` : null].filter(Boolean);
  return parts.length ? parts.join(", ") : "regenerated";
}

// Every regeneration after the first is an entry: when, what was added
// (always visible), and the model's account of what moved (collapsible).
// The newest entry opens by default; anything the detective folds stays
// folded on this browser.
function BriefUpdateLog({ detail, since, clearing }: { detail: InvestigationDetail; since?: string; clearing?: boolean }) {
  const storageKey = `pnai-brief-updates:${detail.id}`;
  const [versions, setVersions] = useState<BriefVersionItem[]>([]);
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "{}");
    } catch {
      return {};
    }
  });
  const generatedAt = detail.brief?.generatedAt;

  useEffect(() => {
    api.briefVersions(detail.id).then(setVersions).catch(() => {});
  }, [detail.id, generatedAt]);

  const entries = versions.filter((v) => v.version > 1 && ((v.brief.whatChanged && v.brief.whatChanged.length) || v.newFiles.length));
  if (!entries.length) return null;
  const latest = entries[0].version;

  const isOpen = (v: number) => (v in collapsed ? !collapsed[v] : v === latest);
  const toggle = (v: number) => {
    const next = { ...collapsed, [v]: isOpen(v) };
    setCollapsed(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {}
  };

  return (
    <div className="mt-5 rounded-2xl border border-base-300 overflow-hidden divide-y divide-base-300">
      {entries.map((v) => {
        const open = isOpen(v.version);
        const unseen = isNewSince(v.generated_at, since);
        return (
          <div key={v.id} className={cn("transition-[background-color,box-shadow] duration-[650ms] ease-out", open ? "bg-blue-50/60 dark:bg-blue-900/20" : "bg-base-100", unseen && !clearing && "ring-1 ring-inset ring-blue-200 dark:ring-blue-900/60")}>
            <button type="button" onClick={() => toggle(v.version)} className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-base-200/60" aria-expanded={open}>
              {unseen && <NewPill clearing={clearing} />}
              <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300 whitespace-nowrap">
                {v.generated_at ? new Date(v.generated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : `v${v.version}`}
              </span>
              <span className="text-sm text-base-content/80 truncate">{describeAdded(v)}</span>
              <ChevronDownIcon className={cn("ml-auto size-4 shrink-0 text-base-content/50 transition-transform", open && "rotate-180")} />
            </button>
            {open && (
              <div className="px-5 pb-4 space-y-3">
                {v.newFiles.length > 0 && (
                  <ul className="flex flex-wrap gap-1.5">
                    {v.newFiles.map((f) => {
                      const Icon = KIND_ICON[f.kind] || DocumentIcon;
                      const chip = "inline-flex items-center gap-1.5 max-w-full rounded-lg border border-base-300 bg-base-100 px-2 py-1 text-xs text-base-content/80";
                      return (
                        <li key={f.id} className="max-w-full">
                          {f.callId ? (
                            <Link to={`/investigations/${detail.id}/calls/${f.callId}`} className={cn(chip, "hover:border-blue-300 hover:text-blue-700 dark:hover:text-blue-300")} title="Open call">
                              <Icon className="size-3.5 shrink-0" />
                              <span className="truncate">{f.name}</span>
                              {f.callDate && <span className="text-base-content/50 shrink-0">{new Date(f.callDate).toLocaleDateString()}</span>}
                            </Link>
                          ) : (
                            <Link to={`/investigations/${detail.id}/files/${f.id}`} className={cn(chip, "hover:border-blue-300 hover:text-blue-700 dark:hover:text-blue-300")} title="Open file">
                              <Icon className="size-3.5 shrink-0" />
                              <span className="truncate">{f.name}</span>
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {v.brief.whatChanged && v.brief.whatChanged.length > 0 && (
                  <ul className="space-y-1.5">
                    {v.brief.whatChanged.map((c, i) => (
                      <li key={i} className="flex gap-2 text-[15px] leading-6 text-base-content/90">
                        <span className="text-blue-600 dark:text-blue-300 shrink-0">•</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SectionTitle({ n, title, subtitle }: { n: string; title: string; subtitle?: string }) {
  return (
    <div className="flex items-baseline gap-3 border-b border-base-300 pb-2">
      <span className="text-xs font-semibold tabular-nums text-blue-600 dark:text-blue-300">{n}</span>
      <h2 className={cn("text-lg font-semibold tracking-tight text-base-content")}>{title}</h2>
      {subtitle && <span className="text-sm text-base-content/50">{subtitle}</span>}
    </div>
  );
}

export default BriefTab;
